import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CommunicationService } from '../../communication/communication.service';
import { take } from 'rxjs/operators';
import { DropdownDisplayItem } from '../../models/data-structures/dropdown-display-item';
import { FeedstockMaterial } from '../../models/data-structures/materials/materials-data.model';

@Injectable({
  providedIn: 'root'
})
export class MaterialService {
  /** A notifier that is emitted when this component is destroyed */
  onDestroyNotifier = new EventEmitter();
  /** An array of FeedstockMaterial; this is the primary container for feedstock materials */
  feedstockMaterials: FeedstockMaterial[] = null;
  /** A notifier that signals with the feedstock materials have been loaded */
  feedstockMaterialsLoadedNotifier = new BehaviorSubject<boolean>(false);

  constructor(private commService: CommunicationService) {
    this.loadMaterials();
  }

  /**
   * Retrieve feedstock and store them in a nested dictionary
   */
  loadMaterials() {
    this.commService.getAllRequest('/dbapi/materials/feedstockMaterialData')
      .pipe(take(1))
      .subscribe(
        (data) => {
          if (data && data['materialData']) {
            this.feedstockMaterials = [];
            for (const item of data['materialData']) {
              this.feedstockMaterials.push(new FeedstockMaterial(item));
            }
            this.feedstockMaterialsLoadedNotifier.next(true);
          }
        }
      );
  }

  /**
   * returns an array of all Feedstock materials
   */
  getFeedstockMaterials(): FeedstockMaterial[] {
    if (this.feedstockMaterials) {
      return this.feedstockMaterials;
    }
    throw Error('material category data no loaded');
  }

  /**
   * Returns a sorted array of feedstock material categories
   */
  getMaterialCategories(): DropdownDisplayItem[] {
    const categories: DropdownDisplayItem[] = [];
    if (this.checkMaterialsLoaded() && this.feedstockMaterials) {
      for (const material of this.feedstockMaterials) {
        if (!categories.find(cat => {
          return cat.value === material.category;
        })) {
          categories.push(new DropdownDisplayItem(material.catTranslationId, material.category));
        }
      }
      categories.push(FeedstockMaterial.getUnknownDisplayItem());
    }
    // TODO replace this with sorting done in HTML after translations
    categories.sort(function (a, b) {
      if (a.value < b.value) { return -1; }
      if (a.value > b.value) { return 1; }
      return 0;
    });
    return categories;
  }

  /**
   * Returns a sorted array of feedstock material types for the provided category.
   * @param category determines the types that are returned
   */
  getMaterialTypesForCat(category: string): DropdownDisplayItem[] {
    if (category === 'unknown') {
      return [FeedstockMaterial.getUnknownDisplayItem()];
    }
    const types: DropdownDisplayItem[] = [];
    if (this.checkMaterialsLoaded() && this.feedstockMaterials) {
      const filteredMaterials = this.feedstockMaterials.filter(
        material => material.category === category
      );
      if (filteredMaterials && filteredMaterials.length > 0) {
        for (const material of filteredMaterials) {
          if (!types.find(type => {
            return material.type === type.value;
          })) {
            types.push(new DropdownDisplayItem(material.typeTranslationId, material.type));
          }
        }
      }
    }
    // TODO replace this with sorting done in HTML after translations
    types.sort(function (a, b) {
      if (a.value < b.value) { return -1; }
      if (a.value > b.value) { return 1; }
      return 0;
    });
    return types;
  }

  /**
   * Returns a sorted array of feedstock material sources for the provided category and type.
   * @param category is used in conjunction with the type to determine the returned sources
   * @param type is used in conjunction with the category to determine the returned sources
   */
  getMaterialSourcesForType(category: string, type: string): DropdownDisplayItem[] {
    if (category === 'unknown' || type === 'unknown') {
      return [FeedstockMaterial.getUnknownDisplayItem()];
    }
    const sources: DropdownDisplayItem[] = [];
    if (this.checkMaterialsLoaded() && this.feedstockMaterials) {
      const filteredMaterials = this.feedstockMaterials.filter(
        material => material.category === category && material.type === type
      );
      if (filteredMaterials && filteredMaterials.length > 0) {
        for (const material of filteredMaterials) {
          sources.push(new DropdownDisplayItem(material.sourceTranslationId, material.source));
        }
      }
    }
    // TODO replace this with sorting done in HTML after translations
    sources.sort(function (a, b) {
      if (a.value < b.value) { return -1; }
      if (a.value > b.value) { return 1; }
      return 0;
    });
    return sources;
  }

  /**
   * Returns true if materials are loaded. If materials are not loaded an error is thrown to
   * interrupt and notifier that a call is being made improperly
   */
  private checkMaterialsLoaded() {
    if (!this.feedstockMaterialsLoadedNotifier.value) {
      throw Error('feedstock materials not loaded');
    }
    return true;
  }

  getMaterialById(materialId: string) {
    if (materialId) {
      return this.feedstockMaterials.find(material => material.id === materialId);
    }
    throw Error('unable to get material by id');
  }

  getMaterialByValues(category: string, type: string, source: string) {
    if (category && type && source) {
      return this.feedstockMaterials.find(material => {
        return material.category === category && material.type === type && material.source === source;
      });
    }
    if (category && type) { // source not specified
      return this.feedstockMaterials.find(material => {
        return material.category === category && material.type === type;
      });
    }
    throw Error('unable to get material by category, type, and source');
  }
}
